import { useRef, useState, type ChangeEvent } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Button,
  IconButton,
  Image,
  AspectRatio,
  Badge,
  Divider,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, StarIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  usePlaylist,
  useFavorites,
  useDeletePlaylist,
  useDuplicatePlaylist,
  useReorderTracks,
  useRemoveTrack,
  useUploadCover,
  useDeleteCover,
} from '../hooks/usePlaylists';
import { SortableTrackList } from '../components/SortableTrackList';
import { PreviewButton } from '../components/AudioPreview';
import { EditPlaylistModal } from '../components/EditPlaylistModal';
import { AddTracksModal } from '../components/AddTracksModal';
import { CoverImageCropper } from '../components/CoverImageCropper';
import { formatTrackDuration, formatTotalDuration } from '../utils';

export function PlaylistDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();

  const { data: playlist, isLoading, isError } = usePlaylist(id);
  const { favoriteIds, toggleFavorite } = useFavorites();
  const deletePlaylist = useDeletePlaylist();
  const duplicatePlaylist = useDuplicatePlaylist();
  const reorderTracks = useReorderTracks(id ?? '');
  const removeTrack = useRemoveTrack(id ?? '');
  const uploadCover = useUploadCover(id ?? '');
  const deleteCover = useDeleteCover(id ?? '');

  const editModal = useDisclosure();
  const addModal = useDisclosure();
  const cropper = useDisclosure();
  const fileRef = useRef<HTMLInputElement>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  if (isLoading) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="xl" color="brand.500" thickness="3px" />
      </Flex>
    );
  }

  if (isError || !playlist) {
    return (
      <VStack py={20} spacing={3}>
        <Text fontWeight="600" color="gray.600">
          {t('playlists.notFound')}
        </Text>
        <Button variant="outline" onClick={() => navigate('/library')}>
          {t('playlists.backToLibrary')}
        </Button>
      </VStack>
    );
  }

  const isFavorite = favoriteIds.has(playlist.id);
  const isAd = playlist.playlistType === 'advertisement';
  const isOwn = playlist.source === 'custom';
  const existingContentIds = new Set(playlist.tracks.map((tr) => tr.contentId));

  const onPickCover = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      cropper.onOpen();
    }
    e.target.value = '';
  };

  const onCropped = (file: File) => {
    uploadCover.mutate(file, {
      onSuccess: () => toast({ status: 'success', title: t('playlists.coverUpdated'), duration: 2000 }),
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
    cropper.onClose();
    setCoverFile(null);
  };

  const handleDuplicate = () =>
    duplicatePlaylist.mutate(playlist.id, {
      onSuccess: () => toast({ status: 'success', title: t('playlists.duplicated'), duration: 2000 }),
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });

  const handleDelete = () => {
    if (!window.confirm(t('playlists.deleteConfirmNamed', { name: playlist.name }))) return;
    deletePlaylist.mutate(playlist.id, {
      onSuccess: () => {
        toast({ status: 'success', title: t('playlists.deleted'), duration: 2000 });
        navigate('/library');
      },
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  return (
    <Box>
      <Button
        variant="link"
        color="gray.500"
        leftIcon={<ArrowBackIcon />}
        mb={4}
        onClick={() => navigate('/library')}
      >
        {t('playlists.backToLibrary')}
      </Button>

      <Flex gap={6} align={{ base: 'stretch', sm: 'flex-end' }} direction={{ base: 'column', sm: 'row' }} mb={6}>
        <Box w={{ base: '140px', md: '180px' }} flexShrink={0}>
          <AspectRatio ratio={1}>
            {playlist.thumbnailUrl ? (
              <Image src={playlist.thumbnailUrl} alt={playlist.name} objectFit="cover" borderRadius="lg" />
            ) : (
              <Flex bgGradient="linear(to-br, brand.400, brand.600)" align="center" justify="center" borderRadius="lg">
                <Text fontSize="5xl" fontWeight="800" color="whiteAlpha.900">
                  {playlist.name.charAt(0).toUpperCase()}
                </Text>
              </Flex>
            )}
          </AspectRatio>
          {isOwn && (
            <HStack mt={2} spacing={2}>
              <Button
                size="xs"
                variant="outline"
                flex={1}
                onClick={() => fileRef.current?.click()}
                isLoading={uploadCover.isPending}
              >
                {t('playlists.changeCover')}
              </Button>
              {playlist.thumbnailUrl && (
                <Button
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  onClick={() =>
                    deleteCover.mutate(undefined, {
                      onSuccess: () => toast({ status: 'success', title: t('playlists.coverRemoved'), duration: 2000 }),
                    })
                  }
                  isLoading={deleteCover.isPending}
                >
                  {t('common.delete')}
                </Button>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickCover} />
            </HStack>
          )}
        </Box>

        <VStack align="stretch" spacing={2} flex={1} minW={0}>
          <HStack spacing={2}>
            {isAd && <Badge colorScheme="orange">{t('playlists.typeAd')}</Badge>}
            {playlist.source === 'library' && <Badge colorScheme="purple">{t('playlists.sourceLibrary')}</Badge>}
          </HStack>
          <Heading size="lg" color="gray.800" noOfLines={2}>
            {playlist.name}
          </Heading>
          {playlist.description && (
            <Text color="gray.500" noOfLines={3}>
              {playlist.description}
            </Text>
          )}
          <Text fontSize="sm" color="gray.500">
            {t('playlists.trackCountLabel', { count: playlist.trackCount })} ·{' '}
            {formatTotalDuration(playlist.totalDurationSeconds)}
          </Text>

          <HStack pt={2}>
            <Button
              leftIcon={<StarIcon />}
              variant={isFavorite ? 'solid' : 'outline'}
              colorScheme={isFavorite ? 'yellow' : 'gray'}
              size="sm"
              onClick={() => toggleFavorite(playlist.id)}
            >
              {isFavorite ? t('playlists.favorited') : t('playlists.favorite')}
            </Button>
            {isOwn && (
              <Button leftIcon={<EditIcon />} variant="outline" size="sm" onClick={editModal.onOpen}>
                {t('playlists.edit')}
              </Button>
            )}
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label={t('common.more', 'More')}
                icon={<Text fontSize="lg" lineHeight={1}>⋮</Text>}
                variant="outline"
                size="sm"
              />
              <MenuList>
                <MenuItem onClick={handleDuplicate}>{t('playlists.duplicate')}</MenuItem>
                <MenuItem color="red.500" onClick={handleDelete}>
                  {t('playlists.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </VStack>
      </Flex>

      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="700" color="gray.600">
          {t('playlists.tracks')}
        </Text>
        {isOwn && (
          <Button size="sm" leftIcon={<AddIcon />} colorScheme="brand" variant="ghost" onClick={addModal.onOpen}>
            {t('playlists.addTracks')}
          </Button>
        )}
      </Flex>
      <Divider mb={2} />

      {playlist.tracks.length === 0 ? (
        <Text color="gray.400" py={8} textAlign="center">
          {t('playlists.noTracks')}
        </Text>
      ) : isOwn ? (
        <SortableTrackList
          tracks={playlist.tracks}
          onReorder={(ids) => reorderTracks.mutate(ids)}
          onRemove={(trackId) => removeTrack.mutate(trackId)}
        />
      ) : (
        <VStack align="stretch" spacing={0} divider={<Divider />}>
          {playlist.tracks.map((track, idx) => (
            <HStack key={track.id} py={2.5} px={2} spacing={4} _hover={{ bg: 'gray.50' }} borderRadius="md">
              <Text w="24px" textAlign="right" color="gray.400" fontSize="sm" flexShrink={0}>
                {idx + 1}
              </Text>
              <Box flex={1} minW={0}>
                <Text fontWeight="500" color="gray.800" noOfLines={1}>
                  {track.title}
                </Text>
                {track.artist && (
                  <Text fontSize="sm" color="gray.500" noOfLines={1}>
                    {track.artist}
                  </Text>
                )}
              </Box>
              <PreviewButton id={track.id} resolveUrl={() => Promise.resolve(track.streamUrl)} size="xs" />
              <Text fontSize="sm" color="gray.400" flexShrink={0}>
                {formatTrackDuration(track.durationSeconds)}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}

      {isOwn && (
        <>
          <EditPlaylistModal playlist={playlist} isOpen={editModal.isOpen} onClose={editModal.onClose} />
          <AddTracksModal
            playlistId={playlist.id}
            isOpen={addModal.isOpen}
            onClose={addModal.onClose}
            existingContentIds={existingContentIds}
          />
          <CoverImageCropper
            file={coverFile}
            isOpen={cropper.isOpen}
            onClose={() => {
              cropper.onClose();
              setCoverFile(null);
            }}
            onCropped={onCropped}
            isSaving={uploadCover.isPending}
          />
        </>
      )}
    </Box>
  );
}

export default PlaylistDetailPage;
